import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { submitRetailInventoryTransfer } from "@/lib/api/retail";
import type {
  RetailInventoryLevel,
  InventoryClassificationSnapshot,
} from "@/features/retail/types/datasets";

type KitchenTransferDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceLevel: RetailInventoryLevel | null;
  classification: InventoryClassificationSnapshot | null;
  productName: string | null;
  locationName: string | null;
  levels: RetailInventoryLevel[];
  onSuccess?: () => void;
};

const DEFAULT_QUANTITY = "1";

const KitchenTransferDialog = ({
  open,
  onOpenChange,
  sourceLevel,
  classification,
  productName,
  locationName,
  levels,
  onSuccess,
}: KitchenTransferDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [targetLevelId, setTargetLevelId] = useState<string | null>(null);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) {
      setQuantity(DEFAULT_QUANTITY);
      setReference("");
      setNote("");
      setTargetLevelId(null);
    }
  }, [open]);

  const targetOptions = useMemo(() => {
    if (!sourceLevel) {
      return [];
    }
    return levels.filter(
      (candidate) =>
        candidate.id !== sourceLevel.id &&
        candidate.classification &&
        candidate.classification.kind !== "retail_product",
    );
  }, [levels, sourceLevel]);

  useEffect(() => {
    const currentTargetId = targetLevelId ?? "";
    if (!targetOptions.some((level) => level.id === currentTargetId)) {
      setTargetLevelId(targetOptions[0]?.id ?? null);
    }
  }, [targetOptions, targetLevelId]);

  const sourceUnit = useMemo(() => {
    if (!classification) {
      return "ea";
    }
    return (
      classification.displayUnit?.abbreviation ??
      classification.unit?.abbreviation ??
      classification.unitId
    );
  }, [classification]);

  const mutation = useMutation({
    mutationFn: submitRetailInventoryTransfer,
    onSuccess: () => {
      toast({
        title: t("retail.inventory.kitchen.toast.transfer", { defaultValue: "Transfer completed." }),
      });
      queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!sourceLevel || !targetLevelId) {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description: t("retail.inventory.toast.missingLevelDescription", {
          defaultValue: "Refresh and try again.",
        }),
        variant: "destructive",
      });
      return;
    }

    const parsedQuantity = Number.parseFloat(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast({
        title: t("retail.quickActions.receive.invalidQuantityTitle", {
          defaultValue: "Invalid quantity",
        }),
        description: t("retail.quickActions.receive.invalidQuantityDescription", {
          defaultValue: "Enter a positive number of units.",
        }),
        variant: "destructive",
      });
      return;
    }

    mutation.mutate({
      sourceLevelId: sourceLevel.id,
      targetLevelId,
      quantity: parsedQuantity,
      reference: reference.trim() ? reference.trim() : null,
      note: note.trim() ? note.trim() : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("retail.inventory.kitchen.dialogs.transfer.title", { defaultValue: "Transfer inventory" })}
          </DialogTitle>
          <DialogDescription>
            {t("retail.inventory.kitchen.dialogs.transfer.description", {
              defaultValue: "Move stock across kitchen locations while keeping ledger history in sync.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {productName ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {locationName ??
                t("retail.receiving.unknownLocation", { defaultValue: "Unassigned location" })}
            </span>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-transfer-target">
              {t("retail.inventory.kitchen.dialogs.transfer.targetLabel", { defaultValue: "Target location" })}
            </Label>
            <Select
              value={targetLevelId ?? ""}
              onValueChange={(value) => setTargetLevelId(value)}
              disabled={mutation.isPending || targetOptions.length === 0}
            >
              <SelectTrigger id="kitchen-transfer-target">
                <SelectValue
                  placeholder={t("retail.inventory.kitchen.dialogs.transfer.targetPlaceholder", {
                    defaultValue: "Select location",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {targetOptions.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    <div className="flex flex-col">
                      <span>{candidate.locationName || t("retail.receiving.unknownLocation", { defaultValue: "Unassigned location" })}</span>
                      <span className="text-xs text-muted-foreground">
                        {candidate.classification?.kind === "prep_item"
                          ? t("retail.inventory.kitchen.kind.prep", { defaultValue: "Prep batch" })
                          : t("retail.inventory.kitchen.kind.ingredient", { defaultValue: "Ingredient" })}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetOptions.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("retail.inventory.kitchen.dialogs.transfer.targetEmpty", {
                  defaultValue: "No other kitchen locations available.",
                })}
              </p>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-transfer-quantity">
              {t("retail.inventory.kitchen.dialogs.transfer.quantityLabel", { defaultValue: "Quantity" })}
            </Label>
            <Input
              id="kitchen-transfer-quantity"
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="1"
              disabled={mutation.isPending}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.dialogs.transfer.quantityHint", {
                defaultValue: "Measured in {{unit}} units.",
                unit: sourceUnit,
              })}
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-transfer-reference">
              {t("retail.inventory.kitchen.dialogs.transfer.referenceLabel", { defaultValue: "Reference (optional)" })}
            </Label>
            <Input
              id="kitchen-transfer-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Transfer ticket"
              disabled={mutation.isPending}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-transfer-note">
              {t("retail.inventory.kitchen.dialogs.transfer.noteLabel", { defaultValue: "Notes (optional)" })}
            </Label>
            <Textarea
              id="kitchen-transfer-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              disabled={mutation.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t("common.actions.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending || targetOptions.length === 0}>
            {mutation.isPending
              ? t("retail.inventory.kitchen.dialogs.transfer.submitPending", { defaultValue: "Transferring…" })
              : t("retail.inventory.kitchen.dialogs.transfer.submitLabel", { defaultValue: "Transfer stock" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KitchenTransferDialog;
